import type { HttpMethod } from 'openapi-typescript-helpers';
import type { Middleware } from 'openapi-fetch';

import createFetchClient from 'openapi-fetch';
import createReactFetchClient from 'openapi-react-query';

import type { components, paths } from './api.generated';
import type { Token } from './lib/authentication';

import { getToken, removeAuthentication } from './lib/authentication';
import { logger } from './lib/logger';
import { serializeQuery, toApiHttpMethod } from './utils';

// ── Error Logging Config ──

type ErrorLoggingConfig = {
    appKind?: components['schemas']['AppKind'] | null;
    appUrl?: string;
    appVersion?: string;
    deviceId?: string;
    deviceName?: string;
    deviceNetwork?: string;
};

let _errorLoggingConfig: ErrorLoggingConfig = {};

export function setErrorLoggingConfig(config: ErrorLoggingConfig): void {
    _errorLoggingConfig = config;
}

// ── Error Logging Middleware ──

const LOG_ENDPOINTS_PREFIX = '/api/v2/apps/logs/';
const BODY_MAX_LENGTH = 4096;
const THROTTLE_COOLDOWN_MS = 10_000;
const THROTTLE_MAP_MAX_SIZE = 100;

type CapturedBody = {
    body?: string | null;
    fileName?: string | null;
    fileContentType?: string | null;
    fileSize?: number | null;
};

const requestBodyMap = new WeakMap<Request, CapturedBody>();
const throttleMap = new Map<string, number>();

function isThrottled(key: string): boolean {
    const last = throttleMap.get(key);
    if (last && Date.now() - last < THROTTLE_COOLDOWN_MS)
        return true;

    if (throttleMap.size > THROTTLE_MAP_MAX_SIZE)
        throttleMap.clear();

    throttleMap.set(key, Date.now());
    return false;
}

function createErrorLoggingMiddleware(): Middleware {
    return {
        async onRequest({ request }) {
            try {
                if (request.url.includes(LOG_ENDPOINTS_PREFIX))
                    return;

                const cloned = request.clone();
                const contentType = cloned.headers.get('content-type') ?? '';
                const captured: CapturedBody = {};

                if (contentType.includes('multipart/form-data')) {
                    try {
                        const formData = await cloned.formData();
                        for (const [, value] of formData.entries()) {
                            if (typeof value === 'object' && value !== null && 'name' in value && 'size' in value) {
                                captured.fileName = (value as { name: string }).name;
                                captured.fileContentType = (value as { type: string }).type;
                                captured.fileSize = (value as { size: number }).size;
                                break;
                            }
                        }
                    }
                    catch { /* ignore */ }
                }
                else {
                    const text = await cloned.text();
                    if (text)
                        captured.body = text.length > BODY_MAX_LENGTH ? text.slice(0, BODY_MAX_LENGTH) : text;
                }

                requestBodyMap.set(request, captured);
            }
            catch { /* never block requests */ }
        },

        async onResponse({ request, response }) {
            try {
                if (response.status < 400)
                    return;

                if (request.url.includes(LOG_ENDPOINTS_PREFIX))
                    return;

                const url = new URL(request.url);
                const path = url.pathname;
                const method = request.method.toUpperCase();
                const throttleKey = `${method}:${path}:${response.status}`;

                if (isThrottled(throttleKey))
                    return;

                const captured = requestBodyMap.get(request) ?? {};
                const queryString = url.search || null;
                const commandName = path.replace(/^\/api\/v2\//, '') || null;
                const requestId = response.headers.get('X-Request-Id') ?? null;
                const message = `HTTP ${response.status} ${response.statusText} on ${method} ${path}`;

                let exception: string | null = null;
                try {
                    exception = await response.clone().text();
                }
                catch { /* ignore */ }

                const token = getToken();
                const baseUrl = url.origin;
                const headers: Record<string, string> = {
                    'Content-Type': 'application/json',
                };
                if (token)
                    headers.Authorization = `Bearer ${token}`;

                const hasRichContext = commandName || captured.body || queryString;
                const httpMethod = toApiHttpMethod(method.toLowerCase() as HttpMethod);

                if (hasRichContext) {
                    const payload: components['schemas']['CreateAppCommandLogCommand'] = {
                        AppKind: _errorLoggingConfig.appKind,
                        AppUrl: _errorLoggingConfig.appUrl ?? null,
                        AppVersion: _errorLoggingConfig.appVersion ?? null,
                        ApiUrl: request.url,
                        ApiHttpMethod: httpMethod,
                        ApiRequestId: requestId,
                        Date: new Date().toISOString(),
                        CommandName: commandName,
                        Path: path,
                        QueryString: queryString,
                        Body: captured.body ?? null,
                        FileName: captured.fileName ?? null,
                        FileContentType: captured.fileContentType ?? null,
                        FileSize: captured.fileSize ?? null,
                        DeviceId: _errorLoggingConfig.deviceId ?? null,
                        DeviceName: _errorLoggingConfig.deviceName ?? null,
                        DeviceNetwork: _errorLoggingConfig.deviceNetwork ?? null,
                        Message: message,
                        Exception: exception,
                        Records: null,
                    };

                    fetch(`${baseUrl}/api/v2/apps/logs/commands/create`, {
                        method: 'POST',
                        headers,
                        body: JSON.stringify(payload),
                    }).catch(() => {});
                }
                else {
                    const payload: components['schemas']['CreateAppLogCommand'] = {
                        AppKind: _errorLoggingConfig.appKind,
                        AppUrl: _errorLoggingConfig.appUrl ?? null,
                        AppVersion: _errorLoggingConfig.appVersion ?? null,
                        ApiUrl: request.url,
                        ApiHttpMethod: httpMethod,
                        ApiRequestId: requestId,
                        LogLevel: 'Error',
                        Date: new Date().toISOString(),
                        DeviceId: _errorLoggingConfig.deviceId ?? null,
                        DeviceName: _errorLoggingConfig.deviceName ?? null,
                        DeviceNetwork: _errorLoggingConfig.deviceNetwork ?? null,
                        Message: message,
                        Exception: exception,
                    };

                    fetch(`${baseUrl}/api/v2/apps/logs/create`, {
                        method: 'POST',
                        headers,
                        body: JSON.stringify(payload),
                    }).catch(() => {});
                }
            }
            catch { /* never block responses */ }
        },
    };
}

/**
 * Create an authentication middleware that adds the token to the Authorization header
 * @param options The options for the middleware
 * @param options.token The token to be used for authentication (optional)
 * @returns The middleware
 * @example
 * const authMiddleware = createAuthMiddleware({ token: "example" });
 *
 * client.use(authMiddleware);
 */
export function createAuthMiddleware({ token: providedToken }: { token?: Token['accessToken'] }): Middleware {
    return {
        async onRequest({ request }) {
            const token = providedToken || getToken();

            if (token)
                request.headers.set('Authorization', `Bearer ${token}`);
        },
        onResponse({ response, request }) {
            if (response.status !== 401)
                return;

            if (request.url.includes('/api/v2/accounts/info'))
                return;

            removeAuthentication();
        },
    };
}

/**
 * Create a middleware that adds a debug header to the request
 * @param headerName The name of the debug header
 * @param headerValue The value of the debug header
 * @returns The middleware
 * @example
 * const debugHeaderMiddleware = createDebugHeaderMiddleware('X-Debug', 'true');
 *
 * client.use(debugHeaderMiddleware);
 */
export function createDebugHeaderMiddleware(headerName: string, headerValue: string): Middleware {
    return {
        async onRequest({ request }) {
            request.headers.set(headerName, headerValue);
        },
    };
}

/**
 * Debuging middleware
 */
export const debugMiddleware: Middleware = {
    async onRequest({ request }) {
        logger.debug(['Request', request]);
    },
    async onResponse({ response }) {
        logger.debug(['Response', response]);
    },
};

export const authMiddleware = createAuthMiddleware({ token: getToken() ?? undefined });

/**
 * The client to make requests to the API
 * @example
 * client.GET("/api/v2/countries/list", {
 *     params: {
 *          query: {
 *              pageNumber: 1,
 *              pageSize: 100,
 *              sort: ["Name", "Alpha2Code", "NumericCodeDesc"],
 *              "select.name": true,
 *              "select.alpha2Code": true,
 *              "select.alpha3Code": true,
 *              "select.numericCode": true,
 *          },
 *      },
 *  });
 */
export const client = createFetchClient<paths>({
    baseUrl: import.meta.env.VITE_API_URL!,
    querySerializer: serializeQuery,
});

export type Client = typeof client;

client.use(authMiddleware);

export const errorLoggingMiddleware = createErrorLoggingMiddleware();
client.use(errorLoggingMiddleware);

export const api = createReactFetchClient(client);

/**
 * Serialize the body to a URLSearchParams object
 * @param body The body to serialize
 * @returns The serialized body
 *
 * @example
 * client.POST("/api/v2/oauth2/token", {
 *  body: {
 *      grant_type: "password",
 *      username: "example",
 *      password: "example",
 *  },
 *  serializeBody, // <-- Add this line
 * });
 */
export function serializeBody(
    body: Record<string, unknown> | undefined | null,
): URLSearchParams {
    const fd = new URLSearchParams();

    for (const key in body) {
        const value = body[key];
        if (value !== undefined) {
            if (Array.isArray(value)) {
                value.forEach(v => fd.append(key, String(v)));
            }
            else {
                fd.append(key, String(value));
            }
        }
    }

    return fd;
}
